import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CatalogService, MarketplaceSeller } from '../../features/catalog/catalog.service';

interface SellerOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-marketplace-seller-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarketplaceSellerSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t(placeholderKey)"
      [showClear]="showClear"
      [filter]="showSearch"
      filterBy="label,id"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class MarketplaceSellerSelectorComponent implements ControlValueAccessor {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'catalog.selectors.seller.placeholder';
  @Input() showSearch = true;
  @Input() showClear = true;
  @Input() approvedOnly = false;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly loading = signal(false);
  readonly options = signal<SellerOption[]>([]);
  readonly value = signal<string | null>(null);
  disabled = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value?.trim() || null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private load(): void {
    this.loading.set(true);
    this.service.listMarketplaceSellers().subscribe({
      next: rows => {
        const filtered = this.approvedOnly
          ? rows.filter(row => row.status === 'Approved')
          : rows;
        this.options.set(filtered.map(row => this.toOption(row)));
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private toOption(row: MarketplaceSeller): SellerOption {
    return { id: row.id, label: `${row.sellerCode} - ${row.displayName}` };
  }
}
